import { GameIcon } from '@/shared/ui/game-icon';
import { type CSSProperties } from 'react';
import type { ActiveBuff } from '@/game/types';
import { BUFF_VISUAL, BUFF_ICON, buffUiStrings } from './buffMeta';
import { useT } from '@/i18n';

interface BuffStatusStripProps {
  buffs: ActiveBuff[];
  nowMs: number;
}

const rowStyle: CSSProperties = {
  display: 'flex',
  gap: 4,
  flexWrap: 'wrap',
  alignItems: 'center',
  width: 'auto',
  maxWidth: '100%',
  minWidth: 0,
  pointerEvents: 'none',
};

const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  padding: '2px 6px',
  borderRadius: 3,
  fontSize: 11,
  fontWeight: 700,
  color: '#e4d8c4',
  textShadow: '0 1px 2px rgba(0,0,0,0.85)',
  flexShrink: 0,
};

function liveRemainingMs(buff: ActiveBuff, nowMs: number): number {
  if (buff.expiresAtMs != null) {
    return Math.max(0, buff.expiresAtMs - nowMs);
  }
  return Math.max(0, buff.remainingMs);
}

/** Read-only active buff indicators (no activation controls). */
export function BuffStatusStrip({ buffs, nowMs }: BuffStatusStripProps) {
  const tr = useT();
  const live = buffs
    .map((b) => ({ ...b, remainingMs: liveRemainingMs(b, nowMs) }))
    .filter((b) => b.remainingMs > 0);

  if (live.length === 0) return null;

  return (
    <div style={rowStyle} role="status" aria-label="Active buffs">
      {live.map((buff) => {
        const meta = BUFF_VISUAL[buff.type];
        if (!meta) return null;
        const { label, desc } = buffUiStrings(tr, buff.type);
        const sec = Math.ceil(buff.remainingMs / 1000);
        const justApplied =
          buff.durationMs > 0 && buff.remainingMs / buff.durationMs > 0.8;
        const style: CSSProperties = {
          ...chipStyle,
          backgroundColor: '#25302d',
          border: `1px solid ${meta.color}aa`,
          animation: justApplied ? 'buff-status-pulse 0.55s ease-out' : undefined,
        };
        return (
          <span key={buff.type} style={style} role="img" title={`${label} — ${desc}`} aria-label={`${label}: ${sec}s`}>
            <GameIcon name={BUFF_ICON[buff.type]} size={13} />
            <span>{sec}s</span>
          </span>
        );
      })}
      <style>{`
        @keyframes buff-status-pulse {
          0%   { transform: scale(1);   opacity: 0.75; }
          50%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
}
